import { create } from 'zustand'
import { createLogger } from '@/lib/logger'
import wexabetMap from '@/lib/wexabetMap'
import { stripWordPunctuation } from '@/lib/wordUtils'

const log = createLogger('store')

/** Extract nuclei (wexabet IDs) from a variant's syllabowls. */
function getNucleiFromVariant(variant) {
  return (variant?.syllabowls || []).map(s => wexabetMap[s.nucleus] ?? 0)
}

function nucleiEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
  return a.every((v, i) => v === b[i])
}

export const useRhymeStore = create((set, get) => ({
  words: {},
  activeWordId: null,

  // Editor highlighting (phoneme/status colors); does not affect Sidebar
  editorHighlightEnabled: true,
  setEditorHighlightEnabled: (enabled) => set({ editorHighlightEnabled: enabled }),

  // Highlight opacity (0–1) for editor word backgrounds
  highlightOpacity: 1,
  setHighlightOpacity: (opacity) => set({ highlightOpacity: Math.max(0, Math.min(1, opacity)) }),

  // Rhyme density: only show syllables that rhyme with nearby lines
  rhymeDensityEnabled: false,
  setRhymeDensityEnabled: (enabled) => set({ rhymeDensityEnabled: enabled }),
  rhymeDensityMap: null, // wordId → boolean[] (per syllabowl, true = active/rhyming)
  setRhymeDensityMap: (map) => set({ rhymeDensityMap: map }),
  rhymeDensityWindowSize: 4, // lines above/below to scan
  setRhymeDensityWindowSize: (n) => set({ rhymeDensityWindowSize: Math.max(1, Math.min(16, n)) }),
  rhymeDensityMinMatches: 1, // minimum other matching syllables to count as active
  setRhymeDensityMinMatches: (n) => set({ rhymeDensityMinMatches: Math.max(1, Math.min(20, n)) }),

  // Vowel filter: null = show all, Set() = show none, Set(['AE','EH']) = only those syllables
  highlightFilter: null,
  setHighlightFilter: (filter) => set({ highlightFilter: filter }),
  toggleHighlightFilterPhoneme: (phoneme) => set((state) => {
    const prev = state.highlightFilter
    if (prev === null) {
      const all = new Set(Object.keys(wexabetMap))
      all.delete(phoneme)
      return { highlightFilter: all }
    }
    const next = new Set(prev)
    if (next.has(phoneme)) next.delete(phoneme)
    else next.add(phoneme)
    return { highlightFilter: next }
  }),

  // Phoneme editing session — replaces editorMode, selectedSegmentIndex, setSegmentVowel, revertPhonemeEdits
  // When active: { wordId, segmentIndex, pendingSyllabowls: [...], pendingNuclei: [...] }
  phonemeSession: null,

  // Mirrored from useLiteMode at the React boundary. When true, enterPhonemeSession
  // is a no-op — variant editing is disabled in mobile lite mode regardless of
  // which seam (Tab key, paste, drop, IME, programmatic) tried to enter the
  // session. Pushed via setLiteMode from a SimpleEditor effect that observes
  // the hook's reactive boolean.
  liteMode: false,
  setLiteMode: (value) => set((state) => {
    const next = !!value
    // Force-clear any active phoneme session when entering lite mode. Without
    // this, a desktop user mid-edit who rotates across the 900px boundary
    // would land in lite mode with phonemeSession still populated — the
    // remounted SimpleEditor would derive editorMode='phoneme' and silently
    // route letter keys to appendToBuffer instead of inserting them as text.
    if (next && state.phonemeSession !== null) {
      return { liteMode: next, phonemeSession: null }
    }
    return { liteMode: next }
  }),

  registerWord: (id, text) => {
    set((state) => {
      const existing = state.words[id]
      if (existing && existing.graphemes === text) return {}

      // Punctuation-only change: same word phonetically; update graphemes, keep variants, no refetch
      if (existing && stripWordPunctuation(existing.graphemes) === stripWordPunctuation(text)) {
        log.info('register_word', { wordId: id, text, _why: 'punctuation_only' })
        return {
          words: {
            ...state.words,
            [id]: { ...existing, graphemes: text }
          }
        }
      }

      const newSeqId = (existing?.seqId || 0) + 1
      log.info('register_word', { wordId: id, text, seqId: newSeqId })
      return {
        words: {
          ...state.words,
          [id]: {
            ...(existing || {}),
            graphemes: text,
            status: 'dirty',
            rhymes: [],
            syllabowls: [],
            nuclei: [],
            variants: [],
            activeVariantIndex: 0,
            seqId: newSeqId
          }
        }
      }
    })
  },

  enterPhonemeSession: (wordId, segmentIndex = 0) => set((state) => {
    // Central chokepoint — catches every entry seam (Tab, paste, drop, IME,
    // programmatic) without per-seam enumeration drift. The Tab handler also
    // checks ctx.liteMode upstream so it can skip preventDefault, but this
    // guard is the structural backstop.
    if (state.liteMode) return {}
    const word = state.words[wordId]
    if (!word?.syllabowls?.length) return {}
    return {
      phonemeSession: {
        wordId,
        segmentIndex,
        pendingSyllabowls: word.syllabowls.map(s => ({ ...s })),
        pendingNuclei: [...(word.nuclei || [])],
        buffers: word.syllabowls.map(s => s.nucleus || ''),
        bufferDirty: word.syllabowls.map(() => false),
      }
    }
  }),

  appendToBuffer: (char) => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    const { segmentIndex, buffers, bufferDirty, pendingSyllabowls, pendingNuclei } = session
    if (segmentIndex < 0 || segmentIndex >= pendingSyllabowls.length) return {}

    const upper = char.toUpperCase()
    let newBuffer
    if (!bufferDirty[segmentIndex]) {
      // First keystroke clears pre-fill
      newBuffer = upper
    } else if (buffers[segmentIndex].length >= 3) {
      return {} // Max 3 chars
    } else {
      newBuffer = buffers[segmentIndex] + upper
    }

    const newBuffers = buffers.map((b, i) => i === segmentIndex ? newBuffer : b)
    const newDirty = bufferDirty.map((d, i) => i === segmentIndex ? true : d)

    // Sync nucleus: valid wexabet key → set it, otherwise → ''
    const nucleus = newBuffer in wexabetMap ? newBuffer : ''
    const newSyllabowls = pendingSyllabowls.map((s, i) =>
      i === segmentIndex ? { ...s, nucleus } : s
    )
    const newNuclei = pendingNuclei.map((v, i) =>
      i === segmentIndex ? nucleus : v
    )

    return {
      phonemeSession: {
        ...session,
        buffers: newBuffers,
        bufferDirty: newDirty,
        pendingSyllabowls: newSyllabowls,
        pendingNuclei: newNuclei,
        alignmentError: null,
      }
    }
  }),

  backspaceBuffer: () => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    const { segmentIndex, buffers, bufferDirty, pendingSyllabowls, pendingNuclei } = session
    if (segmentIndex < 0 || segmentIndex >= pendingSyllabowls.length) return {}
    if (!bufferDirty[segmentIndex] || buffers[segmentIndex].length === 0) {
      // If not dirty yet, clear the pre-fill on backspace
      if (!bufferDirty[segmentIndex] && buffers[segmentIndex].length > 0) {
        const newBuffers = buffers.map((b, i) => i === segmentIndex ? '' : b)
        const newDirty = bufferDirty.map((d, i) => i === segmentIndex ? true : d)
        const newSyllabowls = pendingSyllabowls.map((s, i) =>
          i === segmentIndex ? { ...s, nucleus: '' } : s
        )
        const newNuclei = pendingNuclei.map((v, i) =>
          i === segmentIndex ? '' : v
        )
        return {
          phonemeSession: { ...session, buffers: newBuffers, bufferDirty: newDirty, pendingSyllabowls: newSyllabowls, pendingNuclei: newNuclei, alignmentError: null }
        }
      }
      return {} // Already empty
    }

    const newBuffer = buffers[segmentIndex].slice(0, -1)
    const newBuffers = buffers.map((b, i) => i === segmentIndex ? newBuffer : b)
    const nucleus = newBuffer && newBuffer in wexabetMap ? newBuffer : ''
    const newSyllabowls = pendingSyllabowls.map((s, i) =>
      i === segmentIndex ? { ...s, nucleus } : s
    )
    const newNuclei = pendingNuclei.map((v, i) =>
      i === segmentIndex ? nucleus : v
    )

    return {
      phonemeSession: { ...session, buffers: newBuffers, pendingSyllabowls: newSyllabowls, pendingNuclei: newNuclei, alignmentError: null }
    }
  }),

  cycleSessionSegment: () => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    const next = (session.segmentIndex + 1) % session.pendingSyllabowls.length
    return { phonemeSession: { ...session, segmentIndex: next, alignmentError: null } }
  }),

  selectSessionSegment: (index) => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    return { phonemeSession: { ...session, segmentIndex: index } }
  }),

  clearPhonemeSession: () => set({ phonemeSession: null }),

  commitPhonemeEdits: () => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    const word = state.words[session.wordId]
    if (!word) return { phonemeSession: null }
    return {
      phonemeSession: null,
      words: {
        ...state.words,
        [session.wordId]: {
          ...word,
          syllabowls: session.pendingSyllabowls,
          nuclei: session.pendingNuclei,
        }
      }
    }
  }),

  commitPhonemeEditsWithChunks: (chunks) => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    const word = state.words[session.wordId]
    if (!word) return { phonemeSession: null }

    const rebuiltSyllabowls = session.pendingSyllabowls.map((s, i) => ({
      ...s,
      graphemeChunk: chunks[i],
    }))

    return {
      phonemeSession: null,
      words: {
        ...state.words,
        [session.wordId]: {
          ...word,
          syllabowls: rebuiltSyllabowls,
          nuclei: session.pendingNuclei,
        }
      }
    }
  }),

  addSegment: () => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    const { segmentIndex, pendingSyllabowls, pendingNuclei, buffers, bufferDirty } = session

    // Max syllables = number of alpha chars in the word
    const word = state.words[session.wordId]
    const alphaCount = (word?.graphemes || '').replace(/[^a-zA-Z]/g, '').length
    if (pendingSyllabowls.length >= alphaCount) return {}

    const insertAt = segmentIndex + 1
    const newSyllabowls = [...pendingSyllabowls]
    newSyllabowls.splice(insertAt, 0, { graphemeChunk: '', nucleus: '', stress: 0 })
    const newNuclei = [...pendingNuclei]
    newNuclei.splice(insertAt, 0, '')
    const newBuffers = [...buffers]
    newBuffers.splice(insertAt, 0, '')
    const newDirty = [...bufferDirty]
    newDirty.splice(insertAt, 0, true)

    return {
      phonemeSession: {
        ...session,
        segmentIndex: insertAt,
        pendingSyllabowls: newSyllabowls,
        pendingNuclei: newNuclei,
        buffers: newBuffers,
        bufferDirty: newDirty,
        alignmentError: null,
      }
    }
  }),

  removeSegment: () => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    const { segmentIndex, pendingSyllabowls, pendingNuclei, buffers, bufferDirty } = session
    if (pendingSyllabowls.length <= 1) return {}

    const newSyllabowls = pendingSyllabowls.filter((_, i) => i !== segmentIndex)
    const newNuclei = pendingNuclei.filter((_, i) => i !== segmentIndex)
    const newBuffers = buffers.filter((_, i) => i !== segmentIndex)
    const newDirty = bufferDirty.filter((_, i) => i !== segmentIndex)
    const newIndex = segmentIndex > 0 ? segmentIndex - 1 : 0

    return {
      phonemeSession: {
        ...session,
        segmentIndex: newIndex,
        pendingSyllabowls: newSyllabowls,
        pendingNuclei: newNuclei,
        buffers: newBuffers,
        bufferDirty: newDirty,
        alignmentError: null,
      }
    }
  }),

  setAlignmentError: (message) => set((state) => {
    const session = state.phonemeSession
    if (!session) return {}
    return { phonemeSession: { ...session, alignmentError: message } }
  }),

  setActiveWordId: (id) => set({
    activeWordId: id,
    phonemeSession: null,
  }),

  setLoading: (id) => {
    log.info('set_loading', { wordId: id })
    set((state) => {
      const currentWord = state.words[id]
      return {
        words: {
          ...state.words,
          [id]: {
            ...currentWord,
            status: 'loading',
            error: null
          }
        }
      }
    })
  },

  setRhymeError: (id, message) => {
    log.info('set_error', { wordId: id, error: message })
    set((state) => {
      const currentWord = state.words[id]
      return {
        words: {
          ...state.words,
          [id]: {
            ...currentWord,
            status: 'error',
            error: message || 'Analysis failed'
          }
        }
      }
    })
  },

  setRhymeData: (id, data, returnedSeqId, nucleiHint) => {
    set((state) => {
      const currentWord = state.words[id]
      if (!currentWord || currentWord.seqId !== returnedSeqId) {
        log.info('stale_rejected', { wordId: id, returnedSeqId, currentSeqId: currentWord?.seqId, _why: 'seqId mismatch; async response arrived after user edited word again' })
        return {}
      }

      const raw = data.variants || []
      const seen = new Map()
      const variants = []
      for (const v of raw) {
        const key = JSON.stringify(getNucleiFromVariant(v))
        if (!seen.has(key)) {
          seen.set(key, true)
          variants.push(v)
        }
      }

      // Variant resolution priority: 1) defaultVariantId (explicit user choice), 2) nucleiHint from Slate node, 3) activeVariantIndex fallback
      const defaultVariantId = data.defaultVariantId ?? null
      let activeIdx = 0
      if (variants.length > 0) {
        if (defaultVariantId) {
          const defaultIdx = variants.findIndex(v => v.id === defaultVariantId)
          if (defaultIdx >= 0) {
            activeIdx = defaultIdx
          } else if (nucleiHint) {
            const hintIdx = variants.findIndex(v => nucleiEqual(getNucleiFromVariant(v), nucleiHint))
            activeIdx = hintIdx >= 0 ? hintIdx : 0
          }
        } else if (nucleiHint) {
          const hintIdx = variants.findIndex(v => nucleiEqual(getNucleiFromVariant(v), nucleiHint))
          activeIdx = hintIdx >= 0 ? hintIdx : Math.min(currentWord.activeVariantIndex ?? 0, variants.length - 1)
        } else {
          activeIdx = Math.min(currentWord.activeVariantIndex ?? 0, variants.length - 1)
        }
      }

      const activeVariant = variants[activeIdx]
      const fallback = { rhymes: data.rhymes ?? [], syllabowls: data.syllabowls ?? [], nuclei: data.nuclei ?? [] }

      log.info('set_rhyme_data', { wordId: id, seqId: returnedSeqId, returnedSeqId, variants: variants.length, defaultVariantId, nucleiHintMatch: nucleiHint ? activeIdx !== 0 || nucleiEqual(nucleiHint, getNucleiFromVariant(variants[0])) : false })

      return {
        words: {
          ...state.words,
          [id]: {
            ...currentWord,
            status: 'success',
            error: null,
            rhymes: activeVariant?.rhymes ?? fallback.rhymes,
            syllabowls: activeVariant?.syllabowls ?? fallback.syllabowls,
            arpabet: data.arpabet || '',
            nuclei: activeVariant?.nuclei ?? fallback.nuclei,
            variants,
            activeVariantIndex: activeIdx,
            defaultVariantId,
          }
        }
      }
    })
  },

  setDefaultVariantId: (wordId, variantId) => {
    set((state) => {
      const currentWord = state.words[wordId]
      if (!currentWord) return {}
      return {
        words: {
          ...state.words,
          [wordId]: { ...currentWord, defaultVariantId: variantId }
        }
      }
    })
  },

  getSeqId: (id) => get().words[id]?.seqId || 0,

  retryRequestedId: null,
  requestRetry: (id) => set({ retryRequestedId: id }),
  clearRetryRequested: () => set({ retryRequestedId: null }),

  variantFeedback: null,
  setVariantFeedback: (payload) => set({ variantFeedback: payload }),
  clearVariantFeedback: () => set({ variantFeedback: null }),

  setActiveVariant: (wordId, variantIndex) => {
    set((state) => {
      const currentWord = state.words[wordId]
      if (!currentWord?.variants || variantIndex < 0 || variantIndex >= currentWord.variants.length) {
        return {}
      }
      const v = currentWord.variants[variantIndex]
      return {
        words: {
          ...state.words,
          [wordId]: {
            ...currentWord,
            activeVariantIndex: variantIndex,
            syllabowls: v.syllabowls || [],
            rhymes: v.rhymes || [],
            nuclei: v.nuclei || [],
          }
        },
        // Clear phoneme session if it was editing this word
        ...(state.phonemeSession?.wordId === wordId ? { phonemeSession: null } : {}),
      }
    })
  },

  /** Bulk-insert word entries (e.g. from internal paste) without marking dirty.
   *  Skips any wordId that already exists in the store. */
  prePopulateWords: (entries) => {
    set((state) => {
      const nextWords = { ...state.words }
      let changed = false
      for (const [id, data] of Object.entries(entries)) {
        if (!nextWords[id]) {
          nextWords[id] = { ...data, seqId: 1 }
          changed = true
        }
      }
      return changed ? { words: nextWords } : {}
    })
  },

  // ── Timing View state ──
  timingPositions: {},
  setTimingPositions: (positions) => set({ timingPositions: positions }),
  timingBpm: 120,
  setTimingBpm: (bpm) => set({ timingBpm: bpm }),

  addVariantAndSetActive: (wordId, variant, returnedSeqId) => {
    set((state) => {
      const currentWord = state.words[wordId]
      if (!currentWord || currentWord.seqId !== returnedSeqId) {
        log.info('stale_rejected', { wordId, returnedSeqId, currentSeqId: currentWord?.seqId, _why: 'Reject if seqId mismatch; user may have changed word during variant creation' })
        return {}
      }
      const newNuclei = getNucleiFromVariant(variant)
      const existingVariants = currentWord.variants || []
      const existingIdx = existingVariants.findIndex(v => nucleiEqual(getNucleiFromVariant(v), newNuclei))
      if (existingIdx >= 0) {
        return {
          words: {
            ...state.words,
            [wordId]: {
              ...currentWord,
              activeVariantIndex: existingIdx,
              syllabowls: existingVariants[existingIdx].syllabowls || [],
              rhymes: existingVariants[existingIdx].rhymes || [],
              nuclei: existingVariants[existingIdx].nuclei || [],
            }
          }
        }
      }
      const variants = [...existingVariants, variant]
      const idx = variants.length - 1
      return {
        words: {
          ...state.words,
          [wordId]: {
            ...currentWord,
            variants,
            activeVariantIndex: idx,
            syllabowls: variant.syllabowls || [],
            rhymes: variant.rhymes || [],
            nuclei: variant.nuclei || [],
          }
        }
      }
    })
  },
}))
